import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { Form } from '@/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@/hooks/use-toast';
import { ContentType, IntegrationV2Query, ProductIdentifier } from '@/gql/graphql';
import { useParams } from 'react-router';
import { getTypedMetadata, isMetaMetadata } from '@/routes/trackings/tracking-utils';
import { useEffect } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  FormLayout,
  FormSection,
  SectionHeader,
  RadioField,
  SectionParagraph
} from './../../components/tracking-details-form/base';
import { FormHeaderSection } from '../../components/tracking-details-form/shared-sections';
import { SaveButton } from '../../components/save-button';
import { useIntegrationStore } from '../../stores/tracking-store';

const formSchema = z.object({
  tagDelivery: z.enum(['GTM', 'DIRECT']),
  contentType: z.enum([ContentType.ProductGroup, ContentType.Product]),
  productIdentifier: z.enum([
    ProductIdentifier.ProductId,
    ProductIdentifier.ProductSku,
    ProductIdentifier.VariantId
  ])
});

export const MetaProductSettingsTab = ({
  onContinue,
  isSetup
}: {
  onContinue: () => void;
  isSetup: boolean;
}) => {
  const { toast } = useToast();
  const params = useParams<{ id: string }>();
  const { integration, isLoading, updateMetadata } = useIntegrationStore();
  const client = useApolloClient();

  const metadata = getTypedMetadata({
    integrationV2: integration as IntegrationV2Query['integrationV2']
  });
  const metaMetadata = isMetaMetadata(metadata) ? metadata : null;

  // Check if the integration is fully configured
  const isConfigured = metaMetadata?.contentType && metaMetadata.productIdentifier;
  const isInitialSetup = !isConfigured || isSetup;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tagDelivery: 'GTM',
      contentType: ContentType.ProductGroup,
      productIdentifier: ProductIdentifier.ProductId
    },
    resetOptions: {
      keepDirtyValues: true
    }
  });

  useEffect(() => {
    if (metaMetadata) {
      form.reset({
        tagDelivery: (metaMetadata.tagDelivery as 'GTM' | 'DIRECT' | null | undefined) ?? 'GTM',
        contentType: metaMetadata.contentType as ContentType.ProductGroup | ContentType.Product,
        productIdentifier: metaMetadata.productIdentifier as
          | ProductIdentifier.ProductId
          | ProductIdentifier.ProductSku
          | ProductIdentifier.VariantId
      });
    }
  }, [metaMetadata, form.reset]);

  const { isDirty } = form.formState;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      await updateMetadata(client, {
        id: params.id!,
        metadata: {
          tagDelivery: values.tagDelivery,
          contentType: values.contentType,
          productIdentifier: values.productIdentifier
        }
      });
      toast({
        title: 'Form submitted',
        description: 'Your Meta integration settings have been saved.',
        variant: 'default'
      });
      onContinue();
    } catch (_) {
      toast({
        title: 'Error',
        description: 'Your Meta integration settings could not be saved.',
        variant: 'destructive'
      });
    }
  };
  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit(onSubmit)(e);
        }}
      >
        <FormLayout>
          <FormHeaderSection title="Product Settings">
            This is where you can customize how we send product data to Meta.
          </FormHeaderSection>
          <FormSection>
            <SectionHeader>What is your Content Type?</SectionHeader>
            <SectionParagraph>
              Product ID is typically the primary identifier in Meta integrations, but this can be
              overridden if needed.
            </SectionParagraph>
            <RadioField
              form={form}
              name="contentType"
              options={[
                {
                  label: 'Product Group',
                  value: ContentType.ProductGroup,
                  badge: 'Recommended'
                },
                {
                  label: 'Product',
                  value: ContentType.Product
                }
              ]}
            />
          </FormSection>
          <FormSection>
            <SectionHeader>What is your Product Identifier?</SectionHeader>
            <SectionParagraph>
              Product ID is typically the primary identifier in Meta integrations, but this can be
              overridden if needed.
            </SectionParagraph>
            <RadioField
              form={form}
              name="productIdentifier"
              options={[
                {
                  label: 'Product ID',
                  value: ProductIdentifier.ProductId,
                  badge: 'Recommended'
                },
                {
                  label: 'Product SKU',
                  value: ProductIdentifier.ProductSku
                },
                {
                  label: 'Variant ID',
                  value: ProductIdentifier.VariantId
                }
              ]}
            />
          </FormSection>
          <FormSection>
            <SectionHeader>How should the browser pixel fire?</SectionHeader>
            <SectionParagraph>
              Through Google Tag Manager, you import Contextual&apos;s container into your own GTM
              account. Directly, Contextual fires the pixel from your storefront itself: no GTM
              account or container needed, and changes take effect within minutes.
            </SectionParagraph>
            <RadioField
              form={form}
              name="tagDelivery"
              options={[
                {
                  label: 'Through Google Tag Manager',
                  value: 'GTM'
                },
                {
                  label: 'Directly from Contextual',
                  value: 'DIRECT',
                  badge: 'No GTM needed'
                }
              ]}
            />
          </FormSection>
          <FormSection>
            <SaveButton isDirty={isDirty} isUpdating={isLoading} isInitialSetup={isInitialSetup} />
          </FormSection>
        </FormLayout>
      </form>
    </Form>
  );
};

export default MetaProductSettingsTab;
